/**
 * ValidityChip — o chip comum de validade (#363, D13).
 *
 * Mostra em que pé está um prazo: o texto, um ícone e a cor andam juntos, então nada é dito só
 * pela cor. Os dias vêm do servidor (negativo = vencido); o chip só os escreve, nunca os calcula,
 * porque conta de data no navegador erra o dia por causa do fuso.
 *
 * As quatro cópias antigas (EPC, EPI, medição, treinamento) não migram aqui.
 */
import React from 'react';
import { Chip, type SxProps, type Theme } from '@mui/material';
import {
  Block as OutOfUseIcon,
  CheckCircle as OkIcon,
  ErrorOutline as BadIcon,
  HourglassEmpty as PendingIcon,
  WarningAmber as WarnIcon,
} from '@mui/icons-material';

export type ValidityCode =
  | 'VENCIDO'
  | 'COM_PENDENCIA'
  | 'A_VENCER'
  | 'SEM_INSPECAO'
  | 'EM_DIA'
  | 'FORA_DE_USO';

export interface ValidityChipProps {
  /** O código de situação que o servidor devolveu; decide cor e ícone. */
  situation?: ValidityCode;
  /** Dias até o vencimento, calculados no servidor; negativo = vencido. */
  days?: number | null;
  /** Texto do chip. Sem ele, o texto sai dos dias ("Vence em 13 dias"). */
  label?: string;
}

type Tone = 'success' | 'warning' | 'error' | 'info';

const TONE_ICONS: Record<Tone, React.ReactElement> = {
  success: <OkIcon />,
  warning: <WarnIcon />,
  error: <BadIcon />,
  info: <PendingIcon />,
};

const TONE_BY_SITUATION: Record<ValidityCode, Tone> = {
  VENCIDO: 'error',
  COM_PENDENCIA: 'error',
  A_VENCER: 'warning',
  SEM_INSPECAO: 'info',
  EM_DIA: 'success',
  FORA_DE_USO: 'info',
};

/** Sem situação, o sinal dos dias é tudo o que se sabe: negativo vence, o resto está em dia. */
function toneFromDays(days: number): Tone {
  return days < 0 ? 'error' : 'success';
}

function pluralDays(count: number): string {
  return `${count} ${count === 1 ? 'dia' : 'dias'}`;
}

function labelFromDays(days: number): string {
  if (days < 0) return `Venceu há ${pluralDays(Math.abs(days))}`;
  if (days === 0) return 'Vence hoje';
  return `Vence em ${pluralDays(days)}`;
}

/**
 * Texto e borda no tom escuro da paleta (o `main` do aviso e do sucesso fica abaixo de 4,5:1
 * sobre o branco); no tema escuro volta o tom claro.
 */
const readableChipSx =
  (tone: Tone): SxProps<Theme> =>
  (theme) => ({
    fontSize: '1rem',
    fontWeight: 500,
    color: theme.palette[tone].dark,
    borderColor: theme.palette[tone].dark,
    '& .MuiChip-icon': { color: 'inherit' },
    ...theme.applyStyles('dark', {
      color: theme.palette[tone].light,
      borderColor: theme.palette[tone].light,
    }),
  });

export const ValidityChip: React.FC<ValidityChipProps> = ({ situation, days, label }) => {
  const text = label ?? (typeof days === 'number' ? labelFromDays(days) : undefined);
  if (!text) return null;

  const tone: Tone = situation
    ? TONE_BY_SITUATION[situation]
    : typeof days === 'number'
      ? toneFromDays(days)
      : 'info';
  const icon = situation === 'FORA_DE_USO' ? <OutOfUseIcon /> : TONE_ICONS[tone];

  return <Chip size="small" variant="outlined" icon={icon} label={text} sx={readableChipSx(tone)} />;
};

export default ValidityChip;
